// Fee Management Utility Functions

/**
 * Format currency amount to Indian Rupees format
 * @param {number} amount - The amount to format
 * @param {boolean} showSymbol - Whether to show ₹ symbol
 * @returns {string} Formatted amount
 */
export const formatCurrency = (amount, showSymbol = true) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? '₹0' : '0';
  }

  const formattedAmount = new Intl.NumberFormat('en-IN', {
    style: showSymbol ? 'currency' : 'decimal',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

  return showSymbol ? formattedAmount : formattedAmount;
};

/**
 * Calculate due date based on installment schedule
 * @param {Date} startDate - Start date
 * @param {number} months - Number of months to add
 * @returns {Date} Due date
 */
export const calculateDueDate = (startDate, months) => {
  const date = new Date(startDate);
  date.setMonth(date.getMonth() + months);
  return date;
};

/**
 * Format date to DD/MM/YYYY format
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';

  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';

  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Calculate days overdue
 * @param {Date|string} dueDate - Due date
 * @returns {number} Days overdue (negative if not yet due)
 */
export const calculateDaysOverdue = (dueDate) => {
  if (!dueDate) return 0;

  const due = new Date(dueDate);
  const today = new Date();
  const diffTime = today - due;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * Get installment status based on payment and due date
 * @param {number} paidAmount - Amount paid for this installment
 * @param {number} dueAmount - Amount due for this installment
 * @param {Date|string} dueDate - Due date
 * @returns {object} Status object with status, variant, and badge info
 */
export const getInstallmentStatus = (
  paidAmount = 0,
  dueAmount = 0,
  dueDate,
) => {
  const paid = parseFloat(paidAmount) || 0;
  const due = parseFloat(dueAmount) || 0;
  const daysOverdue = calculateDaysOverdue(dueDate);

  if (paid >= due) {
    return {
      status: 'Paid',
      variant: 'success',
      icon: 'check-circle',
      color: '#28a745',
    };
  } else if (paid > 0) {
    return {
      status: 'Partially Paid',
      variant: 'warning',
      icon: 'exclamation-triangle',
      color: '#ffc107',
    };
  } else if (daysOverdue > 0) {
    return {
      status: 'Overdue',
      variant: 'danger',
      icon: 'times-circle',
      color: '#dc3545',
    };
  } else {
    return {
      status: 'Pending',
      variant: 'info',
      icon: 'clock',
      color: '#17a2b8',
    };
  }
};

/**
 * Calculate total fees summary
 * @param {Array} installments - Array of installments
 * @returns {object} Summary with totals
 */
export const calculateFeesSummary = (installments = []) => {
  const summary = installments.reduce(
    (acc, installment) => {
      const dueAmount = parseFloat(installment.amount) || 0;
      const paidAmount = parseFloat(installment.paid_amount) || 0;
      const pendingAmount = Math.max(0, dueAmount - paidAmount);

      acc.totalDue += dueAmount;
      acc.totalPaid += paidAmount;
      acc.totalPending += pendingAmount;

      const daysOverdue = calculateDaysOverdue(installment.due_date);
      if (pendingAmount > 0 && daysOverdue > 0) {
        acc.overdueAmount += pendingAmount;
        acc.overdueCount += 1;
      }

      if (paidAmount >= dueAmount) {
        acc.paidCount += 1;
      } else if (paidAmount > 0) {
        acc.partialCount += 1;
      } else {
        acc.pendingCount += 1;
      }

      return acc;
    },
    {
      totalDue: 0,
      totalPaid: 0,
      totalPending: 0,
      overdueAmount: 0,
      overdueCount: 0,
      paidCount: 0,
      partialCount: 0,
      pendingCount: 0,
    },
  );

  return summary;
};

/**
 * Generate installment schedule
 * @param {number} totalAmount - Total fee amount
 * @param {number} numberOfInstallments - Number of installments
 * @param {Date} startDate - Start date for first installment
 * @param {number} monthlyInterval - Months between installments
 * @returns {Array} Array of installment objects
 */
export const generateInstallmentSchedule = (
  totalAmount,
  numberOfInstallments,
  startDate,
  monthlyInterval = 1,
) => {
  const installments = [];
  const amountPerInstallment = Math.round(totalAmount / numberOfInstallments);
  let remainingAmount = totalAmount;

  for (let i = 0; i < numberOfInstallments; i++) {
    const isLast = i === numberOfInstallments - 1;
    const amount = isLast ? remainingAmount : amountPerInstallment;
    const dueDate = calculateDueDate(startDate, i * monthlyInterval);

    installments.push({
      installment_number: i + 1,
      amount: amount,
      due_date: dueDate,
      paid_amount: 0,
      status: 'pending',
      remarks: '',
    });

    remainingAmount -= amount;
  }

  return installments;
};

/**
 * Validate fee structure data
 * @param {object} feeStructure - Fee structure object
 * @returns {object} Validation result
 */
export const validateFeeStructure = (feeStructure) => {
  const errors = [];

  if (!feeStructure.categories || feeStructure.categories.length === 0) {
    errors.push('At least one fee category is required');
  }

  feeStructure.categories?.forEach((category, index) => {
    if (!category.name || category.name.trim() === '') {
      errors.push(`Category ${index + 1}: Name is required`);
    }

    if (!category.amount || parseFloat(category.amount) <= 0) {
      errors.push(`Category ${index + 1}: Valid amount is required`);
    }
  });

  if (!feeStructure.installments || feeStructure.installments.length === 0) {
    errors.push('At least one installment is required');
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
};

/**
 * Generate receipt number
 * @param {string} prefix - Receipt prefix (like 'RCP')
 * @param {number} sequence - Sequence number
 * @param {string} academicYear - Academic year (like '2023-24')
 * @returns {string} Receipt number
 */
export const generateReceiptNumber = (
  prefix = 'RCP',
  sequence = 1,
  academicYear = '',
) => {
  const year = academicYear
    ? academicYear.replace('-', '')
    : new Date().getFullYear().toString();
  const sequenceStr = sequence.toString().padStart(4, '0');
  return `${prefix}${year}${sequenceStr}`;
};

/**
 * Parse installment remarks for display
 * @param {string} remarks - Comma-separated remarks
 * @returns {Array} Array of remark objects
 */
export const parseInstallmentRemarks = (remarks = '') => {
  if (!remarks || remarks.trim() === '') return [];

  return remarks.split(',').map((remark) => {
    const parts = remark.trim().split(':');
    return {
      date: parts[0]?.trim() || new Date().toLocaleDateString(),
      remark: parts[1]?.trim() || remark.trim(),
      timestamp: Date.now(),
    };
  });
};

/**
 * Format installment remarks for storage
 * @param {Array} remarks - Array of remark objects
 * @returns {string} Comma-separated remarks string
 */
export const formatInstallmentRemarks = (remarks = []) => {
  return remarks.map((r) => `${r.date}:${r.remark}`).join(',');
};
